import { useEffect, useRef, useState } from "react";
import { Link, usePage } from "@inertiajs/react";
import ApiService from "@/Services/ApiService";
import { formatDistanceToNow } from "date-fns";
import UserOne from "../../images/user/user-00.png";

/**
 * Older notifications may have ? baked into the path as %3F
 * (e.g. /admin/people/1%3Ftab=text). Undo that without touching
 * legitimate encoded characters inside path segments.
 */
function normalizeNotificationUrl(url) {
    if (!url || typeof url !== "string") return url;

    const withQueryStart = url.replace(/%3F(?=[^/?#]+=)/gi, "?");
    const qIdx = withQueryStart.indexOf("?");
    if (qIdx === -1) return withQueryStart;

    return (
        withQueryStart.slice(0, qIdx + 1) +
        withQueryStart.slice(qIdx + 1).replace(/%26/gi, "&")
    );
}

const DropdownNotification = () => {
    const { props: sharedProps } = usePage();
    const { buckets, auth, tenantSlug } = sharedProps; // get auth from sharedProps if available

    const profileImagesBucket = buckets?.profile_images?.name ?? "";
    const profileImagesRegion = buckets?.profile_images?.region ?? "";
    const profileImageBaseUrl = `https://${profileImagesBucket}.s3.${profileImagesRegion}.amazonaws.com/${tenantSlug}/`;

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [notifying, setNotifying] = useState(false);
    const [notifications, setNotifications] = useState([]);

    const trigger = useRef(null);
    const dropdown = useRef(null);

    useEffect(() => {
        // Only fetch notifications if user is logged in
        if (!auth || !auth.user) return;

        const fetchNotifications = async () => {
            try {
                const response = await ApiService.get(
                    route("internal.notifications.get_notifications"),
                );
                setNotifications(response.notifications || []);
                if (
                    !response.notifications ||
                    response.notifications.length === 0
                ) {
                    setNotifying(false);
                } else {
                    setNotifying(true);
                }
            } catch (e) {
                console.error("Notifications API error:", e);
                setNotifications([]);
                setNotifying(false);
            }
        };

        fetchNotifications();
        const interval = setInterval(fetchNotifications, 10000);
        return () => clearInterval(interval);
    }, [auth]);

    useEffect(() => {
        const clickHandler = (event) => {
            if (!dropdown.current) return;
            if (
                !dropdownOpen ||
                dropdown.current.contains(event.target) ||
                trigger.current.contains(event.target)
            )
                return;
            setDropdownOpen(false);
        };
        document.addEventListener("click", clickHandler);
        return () => document.removeEventListener("click", clickHandler);
    });

    useEffect(() => {
        const keyHandler = (event) => {
            if (!dropdownOpen || event.keyCode !== 27) return;
            setDropdownOpen(false);
        };
        document.addEventListener("keydown", keyHandler);
        return () => document.removeEventListener("keydown", keyHandler);
    });

    return (
        <li className="relative">
            <Link
                ref={trigger}
                onClick={(event) => {
                    event.preventDefault();
                    setDropdownOpen(!dropdownOpen);
                }}
                to="#"
                className="relative flex h-8.5 w-8.5 items-center justify-center rounded-full border-[0.5px] border-stroke bg-gray hover:text-primary dark:border-strokedark dark:bg-meta-4 dark:text-white"
            >
                <span
                    className={`absolute -top-0.5 right-0 z-1 h-2 w-2 rounded-full bg-meta-1 ${
                        notifying === false ? "hidden" : "inline"
                    }`}
                >
                    <span className="absolute -z-1 inline-flex h-full w-full animate-ping rounded-full bg-meta-1 opacity-75"></span>
                </span>

                <svg
                    className="fill-current duration-300 ease-in-out"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M16.1999 14.9343L15.6374 14.0624C15.5249 13.8937 15.4687 13.7249 15.4687 13.528V7.67803C15.4687 6.01865 14.7655 4.47178 13.4718 3.31865C12.4312 2.39053 11.0812 1.7999 9.64678 1.6874V1.1249C9.64678 0.787402 9.36553 0.478027 8.9999 0.478027C8.6624 0.478027 8.35303 0.759277 8.35303 1.1249V1.65928C8.29678 1.65928 8.24053 1.65928 8.18428 1.6874C4.92178 2.05303 2.4749 4.66865 2.4749 7.79053V13.528C2.44678 13.8093 2.39053 13.9499 2.33428 14.0343L1.7999 14.9343C1.63115 15.2155 1.63115 15.553 1.7999 15.8343C1.96865 16.0874 2.2499 16.2562 2.55928 16.2562H8.38115V16.8749C8.38115 17.2124 8.6624 17.5218 9.02803 17.5218C9.36553 17.5218 9.6749 17.2405 9.6749 16.8749V16.2562H15.4687C15.778 16.2562 16.0593 16.0874 16.228 15.8343C16.3968 15.553 16.3968 15.2155 16.1999 14.9343ZM3.23428 14.9905L3.43115 14.653C3.5999 14.3718 3.68428 14.0343 3.74053 13.6405V7.79053C3.74053 5.31553 5.70928 3.23428 8.3249 2.95303C9.92803 2.78428 11.503 3.2624 12.6562 4.2749C13.6687 5.1749 14.2312 6.38428 14.2312 7.67803V13.528C14.2312 13.9499 14.3437 14.3437 14.5968 14.7374L14.7655 14.9905H3.23428Z"
                        fill=""
                    />
                </svg>
            </Link>

            <div
                ref={dropdown}
                onFocus={() => setDropdownOpen(true)}
                onBlur={() => setDropdownOpen(false)}
                className={`absolute -right-27 mt-2.5 flex h-90 w-75 flex-col rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark sm:right-0 sm:w-80 ${
                    dropdownOpen === true ? "block" : "hidden"
                }`}
            >
                <div className="px-4.5 py-3">
                    <h5 className="text-sm font-medium text-bodydark2">
                        Notifications
                    </h5>
                </div>
                <ul className="flex h-auto flex-col overflow-y-auto">
                    {notifications.length === 0 ? (
                        <li>
                            <div className="px-4.5 py-3 text-sm text-gray-400">
                                No new notifications
                            </div>
                        </li>
                    ) : (
                        notifications.map((notification) => {
                            const iconMap = {
                                like: {
                                    emoji: "👍",
                                    bg: "",
                                    text: "",
                                },
                                comment: {
                                    emoji: "💬",
                                    bg: "",
                                    text: "",
                                },
                                follow: {
                                    emoji: "➕",
                                    bg: "",
                                    text: "",
                                },
                                course_enrollment: {
                                    emoji: "📚",
                                    bg: "",
                                    text: "",
                                },
                                course_purchase: {
                                    emoji: "💳",
                                    bg: "",
                                    text: "",
                                },
                                direct_message: {
                                    emoji: "✉️",
                                    bg: "",
                                    text: "",
                                },
                                people_inbound_message: {
                                    emoji: "📩",
                                    bg: "",
                                    text: "",
                                },
                                live: {
                                    emoji: "🔴",
                                    bg: "",
                                    text: "",
                                },
                            };

                            const type = notification.data.type || "default";
                            const icon = iconMap[type] || {
                                emoji: "🔔",
                                bg: "bg-gray-200",
                                text: "text-gray-600",
                            };

                            const actorUsername =
                                notification.data?.username ||
                                notification.data?.user_name ||
                                notification.data?.author?.user_name ||
                                notification.data?.author?.username ||
                                null;

                            const actorDisplayName =
                                notification.data?.display_name ||
                                notification.data?.author?.name ||
                                actorUsername ||
                                "Someone";

                            const notificationUrl = normalizeNotificationUrl(
                                notification.data?.url,
                            );

                            return (
                                <li key={notification.id}>
                                    <div className="flex items-center justify-between gap-3 border-t border-stroke py-3 px-4.5 hover:bg-gray-2 dark:border-strokedark dark:hover:bg-meta-4 transition">
                                        {/* Left Side: Avatar + Content */}
                                        <div className="flex items-start gap-3 flex-1 min-w-0">
                                            {/* Avatar */}
                                            <div className="h-10 w-10 flex-shrink-0">
                                                <img
                                                    className="h-10 w-10 rounded-full object-cover"
                                                    src={
                                                        notification.data
                                                            ?.avatar_image_file_name
                                                            ? profileImageBaseUrl +
                                                              notification.data
                                                                  .avatar_image_file_name
                                                            : notification.data
                                                                  ?.login_image_url ||
                                                              UserOne
                                                    }
                                                    alt="User"
                                                />
                                            </div>

                                            {/* Text Content */}
                                            <div className="flex flex-col gap-1 text-sm text-gray-800 dark:text-white leading-snug min-w-0">
                                                <p className="font-medium">
                                                    {actorUsername ? (
                                                        <Link
                                                            href={`/users/${actorUsername}`}
                                                            className="font-semibold text-primary hover:underline"
                                                        >
                                                            {actorDisplayName}
                                                        </Link>
                                                    ) : (
                                                        <span className="font-semibold text-primary">
                                                            {actorDisplayName}
                                                        </span>
                                                    )}{" "}
                                                    <Link
                                                        href={notificationUrl}
                                                        onClick={async () => {
                                                            try {
                                                                await ApiService.post(
                                                                    route(
                                                                        "internal.notifications.mark_read",
                                                                        {
                                                                            id: notification.id,
                                                                        },
                                                                    ),
                                                                );
                                                            } catch (err) {
                                                                console.error(
                                                                    "Error marking notification read:",
                                                                    err,
                                                                );
                                                            }
                                                        }}
                                                        className="hover:underline"
                                                    >
                                                        <b>
                                                            {
                                                                notification
                                                                    .data
                                                                    .message
                                                            }
                                                        </b>
                                                    </Link>
                                                </p>

                                                {notification.data
                                                    .post_message && (
                                                    <Link
                                                        href={notificationUrl}
                                                        onClick={async () => {
                                                            try {
                                                                await ApiService.post(
                                                                    route(
                                                                        "internal.notifications.mark_read",
                                                                        {
                                                                            id: notification.id,
                                                                        },
                                                                    ),
                                                                );
                                                            } catch (err) {
                                                                console.error(
                                                                    "Error marking notification read:",
                                                                    err,
                                                                );
                                                            }
                                                        }}
                                                        className="text-xs text-gray-500 truncate hover:underline"
                                                    >
                                                        {
                                                            notification.data
                                                                .post_message
                                                        }
                                                    </Link>
                                                )}

                                                <p className="text-xs text-gray-400">
                                                    {formatDistanceToNow(
                                                        new Date(
                                                            notification.created_at,
                                                        ),
                                                        {
                                                            addSuffix: true,
                                                        },
                                                    )}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Right-Aligned Icon */}
                                        <div
                                            className={`flex h-6 w-6 items-center justify-center rounded-full ${icon.bg} ${icon.text} text-base flex-shrink-0 ml-2`}
                                        >
                                            {icon.emoji}
                                        </div>
                                    </div>
                                </li>
                            );
                        })
                    )}
                </ul>
            </div>
        </li>
    );
};

export default DropdownNotification;
